import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Typography,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import { glassCardSx, ACCENT, ACCENT_2, TEXT_PRIMARY, playfulGradient } from './SaaSTokens';

const MotionCard = motion(Card);

const CardHeaderRow = ({
  icon,
  title,
  optionalLabel,
  collapsible,
  expanded,
  onToggle,
}) => (
  <Box
    sx={{
      px: { xs: 2, sm: 2.5, md: 3 },
      py: { xs: 1.5, sm: 2 },
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      borderBottom: expanded ? `1px solid ${alpha(ACCENT, 0.16)}` : 'none',
      background: `linear-gradient(90deg, ${alpha(ACCENT, 0.1)}, ${alpha(ACCENT_2, 0.06)}, transparent)`,
      cursor: collapsible ? 'pointer' : 'default',
      userSelect: collapsible ? 'none' : 'auto',
    }}
    onClick={collapsible ? onToggle : undefined}
    role={collapsible ? 'button' : undefined}
    aria-expanded={collapsible ? expanded : undefined}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: playfulGradient,
        border: `1px solid ${alpha(ACCENT, 0.38)}`,
        color: '#fff',
        boxShadow: `0 0 18px ${alpha(ACCENT, 0.28)}`,
      }}
    >
      {React.isValidElement(icon) ? React.cloneElement(icon, { sx: { fontSize: 17 } }) : icon}
    </Box>

    <Typography
      sx={{
        fontFamily: "'Syne', sans-serif",
        fontSize: { xs: 12, sm: 14 },
        fontWeight: 800,
        color: TEXT_PRIMARY,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        flex: 1,
      }}
    >
      {title}
    </Typography>

    {optionalLabel ? (
      <Chip
        label={optionalLabel}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.62rem',
          fontFamily: "'JetBrains Mono', monospace",
          bgcolor: alpha(ACCENT_2, 0.1),
          color: alpha(ACCENT_2, 0.95),
          border: `1px solid ${alpha(ACCENT_2, 0.2)}`,
        }}
      />
    ) : null}

    {collapsible ? (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        sx={{
          color: alpha(ACCENT, 0.7),
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}
        aria-label={expanded ? 'Collapse section' : 'Expand section'}
      >
        <ExpandMoreIcon fontSize="small" />
      </IconButton>
    ) : null}
  </Box>
);

const SectionCard = ({
  title,
  icon,
  delay = 0,
  pulse = false,
  children,
  sx = {},
  sectionId,
  collapsible = false,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  optionalLabel,
  forceExpanded,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const expanded = forceExpanded ?? (controlledExpanded !== undefined ? controlledExpanded : internalExpanded);

  useEffect(() => {
    if (forceExpanded) setInternalExpanded(true);
  }, [forceExpanded]);

  const setExpanded = (next) => {
    if (controlledExpanded === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  const toggle = () => setExpanded(!expanded);

  return (
    <MotionCard
      id={sectionId}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay, ease: [0.4, 0, 0.2, 1] }}
      elevation={0}
      sx={{ ...glassCardSx, height: '100%', scrollMarginTop: { xs: 120, lg: 96 }, ...sx }}
    >
      {pulse ? (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            width: 8,
            height: 8,
            borderRadius: 999,
            bgcolor: ACCENT,
            boxShadow: `0 0 12px ${alpha(ACCENT, 0.6)}`,
          }}
        />
      ) : null}

      <CardHeaderRow
        icon={icon}
        title={title}
        optionalLabel={optionalLabel}
        collapsible={collapsible}
        expanded={expanded}
        onToggle={toggle}
      />
      <Collapse in={expanded} timeout="auto">
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>{children}</CardContent>
      </Collapse>
    </MotionCard>
  );
};

export default SectionCard;
